/**
 * WP-04B-R — The account bootstrap gate state machine (pure, transport-free).
 *
 * Account bootstrap is a REQUIRED gate for the `/app` subtree. Until the
 * backend account graph is reconciled for the verified identity, the
 * application must not render: a suspended, deactivated, or pending-deletion
 * account, a conflicting account graph, or a privilege defect all keep the user
 * outside the subtree.
 *
 * Phases:
 *   * `loading`     — no decision yet; render nothing of the app.
 *   * `ready`       — reconciled; render the app.
 *   * `blocked`     — `account_not_active`; safe blocked-account screen.
 *   * `failed`      — terminal, fail closed (graph conflict, not authorized,
 *                     unknown subject, invalid input). No retry offered.
 *   * `unavailable` — recoverable; a bounded retry already happened.
 */

import type { AccountBootstrapFailureReason, AccountBootstrapOutcome } from "./bootstrapCommand";

export type AccountGatePhase = "loading" | "ready" | "blocked" | "failed" | "unavailable";

/** Reasons that permanently deny entry and must never offer a retry. */
export const FAIL_CLOSED_REASONS: readonly AccountBootstrapFailureReason[] = [
  "account_graph_conflict",
  "not_authorized",
  "unknown_subject",
  "invalid_subject",
  "invalid_request",
  "invalid_display_name",
  "invalid_time_zone",
];

export function isFailClosedReason(reason: AccountBootstrapFailureReason): boolean {
  return FAIL_CLOSED_REASONS.includes(reason);
}

/** True only when an identical retry is a sensible next step. */
export function shouldRetryOutcome(outcome: AccountBootstrapOutcome): boolean {
  return outcome.ok === false && outcome.retryable && !isFailClosedReason(outcome.reason);
}

/** Maps a settled outcome (or `null`, meaning undecided) onto a gate phase. */
export function phaseForOutcome(outcome: AccountBootstrapOutcome | null): AccountGatePhase {
  if (outcome === null) return "loading";
  if (outcome.ok) return "ready";
  if (outcome.reason === "account_not_active") return "blocked";
  if (isFailClosedReason(outcome.reason)) return "failed";
  return "unavailable";
}

/** The app subtree may render in exactly one phase. */
export function mayEnterApplication(phase: AccountGatePhase): boolean {
  return phase === "ready";
}
